import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Plus, Edit, Trash2, Loader2, Save } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';

const MAPPING_TYPES = [
  { value: 'invoice_materials', label: 'Invoice Materials (Default)', description: 'Default account for invoice materials' },
  { value: 'invoice_labour', label: 'Invoice Labour (Default)', description: 'Default account for invoice labour' },
  { value: 'purchase_default', label: 'Purchases (Default)', description: 'Default account for purchases' },
];

export default function AccountMapping() {
  const navigate = useNavigate();
  const { success, error: showError, ToastContainer } = useToast();
  
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingMapping, setEditingMapping] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    mappingType: '',
    nominalAccountId: '',
    priority: 100,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [accountsData, mappingsData] = await Promise.all([
        accountingAPI.getAccounts({ active_only: 'true' }),
        accountingAPI.getMappings(),
      ]);
      
      setAccounts(accountsData || []);
      setMappings(mappingsData || []);
    } catch (err) {
      setError(err.message || 'Failed to load account mappings');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMapping = async (e) => {
    e.preventDefault();

    if (!formData.mappingType || !formData.nominalAccountId) {
      showError('Error', 'Mapping type and account are required');
      return;
    }

    try {
      await accountingAPI.createMapping(formData);
      success('Success', 'Account mapping created successfully');
      setShowCreateModal(false);
      setFormData({
        mappingType: '',
        nominalAccountId: '',
        priority: 100,
      });
      fetchData();
    } catch (err) {
      showError('Error', err.message || 'Failed to create mapping');
    }
  };

  const handleUpdateMapping = async (mappingId, updates) => {
    try {
      await accountingAPI.updateMapping(mappingId, updates);
      success('Success', 'Account mapping updated successfully');
      setEditingMapping(null);
      fetchData();
    } catch (err) {
      showError('Error', err.message || 'Failed to update mapping');
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!confirm('Are you sure you want to deactivate this mapping?')) {
      return;
    }

    try {
      await accountingAPI.updateMapping(mappingId, { isActive: false });
      success('Success', 'Account mapping deactivated');
      fetchData();
    } catch (err) {
      showError('Error', err.message || 'Failed to deactivate mapping');
    }
  };

  const getMappingTypeLabel = (mappingType) => {
    const mapping = MAPPING_TYPES.find(m => m.value === mappingType);
    return mapping ? mapping.label : mappingType;
  };

  const getMappingTypeDescription = (mappingType) => {
    const mapping = MAPPING_TYPES.find(m => m.value === mappingType);
    return mapping ? mapping.description : '';
  };

  const groupedMappings = mappings.reduce((acc, mapping) => {
    const type = mapping.mappingType.split('_')[0]; // Group by prefix (invoice, purchase, bank_account, supplier, etc.)
    if (!acc[type]) acc[type] = [];
    acc[type].push(mapping);
    return acc;
  }, {});

  return (
    <>
      <ToastContainer />
      <Layout>
        {/* Header */}
        <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                  Account Mapping Configuration
                </h1>
                <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                  Configure which nominal accounts are used for different transaction types
                </p>
              </div>
              <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/reports')}
                >
                  ← Back to Reports
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowCreateModal(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Mapping
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error */}
          {error && (
            <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">
              {error}
            </div>
          )}

          {/* Mappings List */}
          <Card>
            {loading ? (
              <CardContent className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-[var(--ds-accent-primary)]" />
                <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading mappings...</p>
              </CardContent>
            ) : (
              <CardContent className="p-6">
                {mappings.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-xs text-[var(--ds-text-muted)] mb-4">No account mappings configured</p>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Mapping
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(groupedMappings).map(([group, groupMappings]) => (
                      <div key={group}>
                        <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4 capitalize">
                          {group.replace('_', ' ')} Mappings
                        </h3>
                        <Table>
                          <TableHeader>
                            <TableRow isHeader>
                              <TableHead>Mapping Type</TableHead>
                              <TableHead>Account</TableHead>
                              <TableHead align="center">Priority</TableHead>
                              <TableHead align="center">Status</TableHead>
                              <TableHead align="right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {groupMappings.map((mapping) => (
                              <TableRow key={mapping.id}>
                                <TableCell>
                                  <div>
                                    <div className="text-xs font-medium text-[var(--ds-text-primary)]">
                                      {getMappingTypeLabel(mapping.mappingType)}
                                    </div>
                                    <div className="text-xs text-[var(--ds-text-muted)]">
                                      {getMappingTypeDescription(mapping.mappingType) || mapping.mappingType}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {editingMapping === mapping.id ? (
                                    <Select
                                      value={mapping.nominalAccountId || ''}
                                      onChange={(e) => {
                                        handleUpdateMapping(mapping.id, { nominalAccountId: e.target.value });
                                      }}
                                      className="h-8"
                                    >
                                      <option value="">Select account</option>
                                      {accounts.map(account => (
                                        <option key={account.id} value={account.id}>
                                          {account.accountNumber} - {account.accountName}
                                        </option>
                                      ))}
                                    </Select>
                                  ) : (
                                    <div className="text-xs text-[var(--ds-text-primary)]">
                                      <span className="font-mono">{mapping.accountNumber}</span> - {mapping.accountName}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <span className="text-xs text-[var(--ds-text-secondary)]">{mapping.priority}</span>
                                </TableCell>
                                <TableCell align="center">
                                  <Badge variant={mapping.isActive ? 'success' : 'neutral'}>
                                    {mapping.isActive ? 'Active' : 'Inactive'}
                                  </Badge>
                                </TableCell>
                                <TableCell align="right">
                                  <div className="flex justify-end gap-2">
                                    {editingMapping === mapping.id ? (
                                      <Button
                                        variant="outline"
                                        size="icon-sm"
                                        onClick={() => setEditingMapping(null)}
                                        title="Cancel editing"
                                      >
                                        <Save className="h-4 w-4" />
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="outline"
                                        size="icon-sm"
                                        onClick={() => setEditingMapping(mapping.id)}
                                        title="Edit mapping"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="icon-sm"
                                      onClick={() => handleDeleteMapping(mapping.id)}
                                      title="Deactivate mapping"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      </Layout>

      {/* Create Mapping Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create Account Mapping</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateMapping} className="space-y-4">
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Mapping Type <span className="text-[var(--ds-status-danger)]">*</span>
                  </label>
                  <Select
                    value={formData.mappingType}
                    onChange={(e) => setFormData({ ...formData, mappingType: e.target.value })}
                    required
                    className="h-8"
                  >
                    <option value="">Select mapping type</option>
                    {MAPPING_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                  {formData.mappingType && (
                    <p className="mt-1 text-xs text-[var(--ds-text-muted)]">
                      {getMappingTypeDescription(formData.mappingType)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nominal Account <span className="text-[var(--ds-status-danger)]">*</span>
                  </label>
                  <Select
                    value={formData.nominalAccountId}
                    onChange={(e) => setFormData({ ...formData, nominalAccountId: e.target.value })}
                    required
                    className="h-8"
                  >
                    <option value="">Select account</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.accountNumber} - {account.accountName}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Priority
                  </label>
                  <Select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    className="h-8"
                  >
                    <option value="10">10 - Highest</option>
                    <option value="50">50 - High</option>
                    <option value="100">100 - Normal</option>
                    <option value="150">150 - Low</option>
                    <option value="200">200 - Lowest</option>
                  </Select>
                  <p className="mt-1 text-xs text-[var(--ds-text-muted)]">
                    Lower numbers take precedence when multiple mappings match
                  </p>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowCreateModal(false);
                      setFormData({
                        mappingType: '',
                        nominalAccountId: '',
                        priority: 100,
                      });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    className="flex-1"
                  >
                    Create Mapping
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
